import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, unref as _unref, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = [".open", "title", "subtitle"]
const _hoisted_2 = {
  id: "modal-container-element",
  class: "modal-content"
}
const _hoisted_3 = { columns: "2" }
const _hoisted_4 = ["value", "disabled"]
const _hoisted_5 = ["value"]
const _hoisted_6 = ["value"]
const _hoisted_7 = { columns: "1" }
const _hoisted_8 = ["value"]
const _hoisted_9 = { columns: "1" }
const _hoisted_10 = ["value"]
const _hoisted_11 = { columns: "2" }
const _hoisted_12 = ["value"]
const _hoisted_13 = ["value"]
const _hoisted_14 = { columns: "1" }
const _hoisted_15 = ["value"]
const _hoisted_16 = { columns: "2" }
const _hoisted_17 = ["value"]
const _hoisted_18 = ["value"]
const _hoisted_19 = { columns: "1" }
const _hoisted_20 = ["checked"]
const _hoisted_21 = { style: {"display":"flex","align-items":"flex-end","gap":"8px"} }
const _hoisted_22 = ["value", "onTextInputChange"]
const _hoisted_23 = ["onButtonClick"]
const _hoisted_24 = { slot: "footer" }
const _hoisted_25 = ["disabled"]

import { computed, ref } from 'vue';
import { useStore } from 'vuex';

import { EPINIO_TYPES } from '../../types';
import { epinioExceptionToErrorsArray } from '../../utils/errors';
import { validateKubernetesName } from '@shell/utils/validators/kubernetes-name';
import { mapSettingsFromApiResponse, mapSettingsToApiRequest, validateSettings } from '../../utils/settings';
import Banner from '@components/Banner/Banner.vue';
import EpinioCatalogServiceModel from '../../models/catalogservices';
import ChartSettings, { ConfigSetting } from '../settings/ChartSettings.vue';

import isEqual from 'lodash/isEqual';
import sortBy from 'lodash/sortBy';


export default /*@__PURE__*/_defineComponent({
  __name: 'CatalogServiceModal',
  setup(__props, { expose: __expose }) {

const store = useStore() as any;
const t = store.getters['i18n/t'];

// Modal open state
const showModal = ref(false);
const modalMode = ref<'create' | 'edit' | 'view'>('create');

const initialValues = ref<EpinioCatalogServiceModel | null>(null);

// Form fields (separate from the model to avoid proxy mutation issues)
const catalogServiceName = ref('');
const catalogServiceShortDescription = ref('');
const catalogServiceDescription = ref('');
const catalogServiceChart = ref('');
const catalogServiceChartVersion = ref('');
const catalogServiceAppVersion = ref('');
const catalogServiceIcon = ref('');
const catalogServiceHelmRepo = ref({ name: '', url: '', secret: '' });
const chartSettings = ref<ConfigSetting[]>([]);
const catalogServiceSecretTypes = ref<string[]>([]);

const saving = ref(false);
const errors = ref<string[]>([]);
const hasAssociatedServices = ref<boolean>(false);

const isEdit = computed(() => modalMode.value === 'edit');
const isView = computed(() => modalMode.value === 'view');

const showAdvancedOptions = ref<boolean>(false);

const isDirty = computed(() => {
  if (!initialValues.value) {
    return catalogServiceName.value !== '' ||
      catalogServiceShortDescription.value !== '' ||
      catalogServiceDescription.value !== '' ||
      catalogServiceChart.value !== '' ||
      catalogServiceChartVersion.value !== '' ||
      catalogServiceAppVersion.value !== '' ||
      catalogServiceIcon.value !== '' ||
      catalogServiceHelmRepo.value.name !== '' ||
      catalogServiceHelmRepo.value.url !== '' ||
      catalogServiceHelmRepo.value.secret !== '' ||
      chartSettings.value.length > 0 ||
      catalogServiceSecretTypes.value.length > 0;
  }

  const initialSettings = mapSettingsFromApiResponse(initialValues.value);

  const isDirty = catalogServiceName.value !== (initialValues.value!.meta.name || '') ||
    catalogServiceShortDescription.value !== (initialValues.value!.short_description || '') ||
    catalogServiceDescription.value !== (initialValues.value!.description || '') ||
    catalogServiceChart.value !== (initialValues.value!.chart || '') ||
    catalogServiceChartVersion.value !== (initialValues.value!.chart_version || '') ||
    catalogServiceAppVersion.value !== (initialValues.value!.app_version || '') ||
    catalogServiceIcon.value !== (initialValues.value!.service_icon || '') ||
    catalogServiceHelmRepo.value.name !== (initialValues.value!.helm_repo?.name || '') ||
    catalogServiceHelmRepo.value.url !== (initialValues.value!.helm_repo?.url || '') ||
    catalogServiceHelmRepo.value.secret !== (initialValues.value!.helm_repo?.secret || '') ||
    !isEqual(sortBy(chartSettings.value, 'name'), sortBy(initialSettings, 'name')) ||
    !isEqual(sortBy(catalogServiceSecretTypes.value), sortBy(initialValues.value!.secret_types || []));
 
  return isDirty;
});

const showDiscardConfirm = ref(false);

const validationPassed = computed(() => {
  if (!catalogServiceName.value) return false;
  if (!catalogServiceShortDescription.value) return false;
  if (!catalogServiceDescription.value) return false;
  if (!catalogServiceChart.value) return false;
  if (!catalogServiceHelmRepo.value.name) return false;
  if (!catalogServiceHelmRepo.value.url) return false;

  const settingsValidation = validateSettings(chartSettings.value);
  if (!settingsValidation) return false;

  const secretTypesValid = catalogServiceSecretTypes.value.every((type) => type && type.trim() !== '');
  if (!secretTypesValid) return false;

  const nameErrors = validateKubernetesName(catalogServiceName.value, '', store.getters, undefined, []);
  return nameErrors.length === 0;
});

const canSave = computed(() => {
  const dirty = isDirty.value;
  const valid = validationPassed.value;
  return dirty && valid && !saving.value;
});

function openCreate() {
  errors.value = [];
  modalMode.value = 'create';
  catalogServiceName.value = '';
  catalogServiceShortDescription.value = '';
  catalogServiceDescription.value = '';
  catalogServiceChart.value = '';
  catalogServiceChartVersion.value = '';
  catalogServiceAppVersion.value = '';
  catalogServiceIcon.value = '';
  catalogServiceHelmRepo.value = { name: '', url: '', secret: '' };
  chartSettings.value = [];
  catalogServiceSecretTypes.value = [];
  showModal.value = true;
}

function openEdit(row: EpinioCatalogServiceModel) {
  errors.value = [];
  modalMode.value = 'edit';
  initialValues.value = row;
  catalogServiceName.value = row.name || row.meta?.name || '';
  catalogServiceShortDescription.value = row.short_description || '';
  catalogServiceDescription.value = row.description || '';
  catalogServiceChart.value = row.chart || '';
  catalogServiceChartVersion.value = row.chart_version || '';
  catalogServiceAppVersion.value = row.app_version || '';
  catalogServiceIcon.value = row.service_icon || '';
  catalogServiceHelmRepo.value = {
    name: row.helm_repo?.name || '',
    url: row.helm_repo?.url || '',
    secret: row.helm_repo?.secret || ''
  };
  chartSettings.value = mapSettingsFromApiResponse(row);
  catalogServiceSecretTypes.value = row.secret_types || [];
  hasAssociatedServices.value = !!row.bound_services;
  showAdvancedOptions.value = row.secret_types && row.secret_types.length > 0;

  showModal.value = true;
}

function handleModalClose() {
  if (isDirty.value) {
    showDiscardConfirm.value = true;
  } else {
    closeModal();
  }
}

function handleKeepEditing() {
  showDiscardConfirm.value = false;
}

function handleDiscard() {
  showDiscardConfirm.value = false;
  closeModal();
}

function closeModal() {
  // Clear form state before setting showModal = false so that when Lit fires
  // modal-close (which triggers handleModalClose), isDirty is already false
  catalogServiceName.value = '';
  catalogServiceShortDescription.value = '';
  catalogServiceDescription.value = '';
  catalogServiceChart.value = '';
  catalogServiceChartVersion.value = '';
  catalogServiceAppVersion.value = '';
  catalogServiceIcon.value = '';
  catalogServiceHelmRepo.value = { name: '', url: '', secret: '' };
  chartSettings.value = [];
  catalogServiceSecretTypes.value = [];
  errors.value = [];
  showDiscardConfirm.value = false;
  showModal.value = false;
  initialValues.value = null;
  hasAssociatedServices.value = false;
  showAdvancedOptions.value = false;
}

async function onSubmit() {
  if (!validationPassed.value || !isDirty.value || saving.value) return;

  saving.value = true;
  errors.value = [];

  const { settings } = mapSettingsToApiRequest(chartSettings.value);

  try {
    if (isEdit.value && initialValues.value) {
      const catalogService = initialValues.value;

      catalogService.description       = catalogServiceDescription.value;
      catalogService.short_description = catalogServiceShortDescription.value;
      catalogService.chart             = catalogServiceChart.value;
      catalogService.chart_version     = catalogServiceChartVersion.value;
      catalogService.app_version       = catalogServiceAppVersion.value;
      catalogService.service_icon      = catalogServiceIcon.value;
      catalogService.helm_repo         = { ...catalogServiceHelmRepo.value };
      catalogService.settings          = settings;
      catalogService.secret_types      = [...catalogServiceSecretTypes.value];

      await catalogService.update();
      store.dispatch('growl/success', {
        title:   t('epinio.growl.catalogServices.update.success.title'),
        message: t('epinio.growl.catalogServices.update.success.message', { name: catalogServiceName.value }),
      });
      closeModal();
      store.dispatch('epinio/findAll', { type: EPINIO_TYPES.CATALOG_SERVICE, opt: { force: true } }).catch(() => {});
    } else {
      const catalogService = await store.dispatch('epinio/create', { type: EPINIO_TYPES.CATALOG_SERVICE });

      catalogService.metadata          = { name: catalogServiceName.value };
      catalogService.description       = catalogServiceDescription.value;
      catalogService.short_description = catalogServiceShortDescription.value;
      catalogService.chart             = catalogServiceChart.value;
      catalogService.chart_version     = catalogServiceChartVersion.value;
      catalogService.app_version       = catalogServiceAppVersion.value;
      catalogService.service_icon      = catalogServiceIcon.value;
      catalogService.helm_repo         = { ...catalogServiceHelmRepo.value };
      catalogService.settings          = settings;
      catalogService.secret_types      = [...catalogServiceSecretTypes.value];

      await catalogService.create();
      store.dispatch('growl/success', {
        title:   t('epinio.growl.catalogServices.create.success.title'),
        message: t('epinio.growl.catalogServices.create.success.message', { name: catalogServiceName.value }),
      });
      closeModal();
      store.dispatch('epinio/findAll', { type: EPINIO_TYPES.CATALOG_SERVICE, opt: { force: true } }).catch(() => {});
    }
  } catch (err: any) {
    errors.value = epinioExceptionToErrorsArray(err);
    store.dispatch('growl/error', {
      title: isEdit.value
        ? t('epinio.growl.catalogServices.save.error.updateTitle')
        : t('epinio.growl.catalogServices.save.error.createTitle'),
      message: t('epinio.growl.catalogServices.save.error.message'),
    });
    console.error('Error saving catalog service:', err);
  } finally {
    saving.value = false;
  }
}

__expose({ openCreate, openEdit });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("trailhand-modal", {
    ".open": showModal.value,
    ".dismissible": false,
    title: (isView.value || isEdit.value) ? initialValues.value?.meta?.name || 'Catalog Service' : 'Catalog Service',
    subtitle: (isView.value || isEdit.value) ? '' : 'Create New',
    position: "top",
    onModalClose: handleModalClose
  }, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("trailhand-form-card", null, [
        (hasAssociatedServices.value)
          ? (_openBlock(), _createBlock(Banner, {
              key: 0,
              color: "warning",
              label: "This catalog service is currently associated with one or more services. Editing it may cause issues for those services."
            }))
          : _createCommentVNode("", true),
        _cache[21] || (_cache[21] = _createTextVNode()),
        _createElementVNode("trailhand-form-row", _hoisted_3, [
          _createElementVNode("trailhand-text-input", {
            value: catalogServiceName.value,
            label: "Name",
            placeholder: "A Unique Name",
            required: true,
            disabled: isEdit.value,
            onTextInputChange: _cache[0] || (_cache[0] = (e) => { catalogServiceName.value = e.detail.value; })
          }, null, 40, _hoisted_4),
          _cache[14] || (_cache[14] = _createTextVNode()),
          _createElementVNode("trailhand-text-input", {
            value: catalogServiceShortDescription.value,
            label: "Short Description",
            placeholder: "A brief description",
            required: true,
            onTextInputChange: _cache[1] || (_cache[1] = (e) => { catalogServiceShortDescription.value = e.detail.value; })
          }, null, 40, _hoisted_5)
        ]),
        _cache[22] || (_cache[22] = _createTextVNode()),
        _createElementVNode("trailhand-form-row", null, [
          _createElementVNode("trailhand-text-area", {
            value: catalogServiceDescription.value,
            label: "Description",
            placeholder: "A detailed description",
            required: "",
            onTextAreaChange: _cache[2] || (_cache[2] = (e) => { catalogServiceDescription.value = e.detail.value; })
          }, null, 40, _hoisted_6)
        ]),
        _cache[23] || (_cache[23] = _createTextVNode()),
        _createElementVNode("trailhand-form-row", _hoisted_7, [
          _createElementVNode("trailhand-text-input", {
            value: catalogServiceIcon.value,
            label: "Icon URL",
            placeholder: "e.g. registry.example.com/builder:latest",
            onTextInputChange: _cache[3] || (_cache[3] = (e) => { catalogServiceIcon.value = e.detail.value; })
          }, null, 40, _hoisted_8)
        ]),
        _cache[24] || (_cache[24] = _createTextVNode()),
        _createElementVNode("trailhand-form-row", _hoisted_9, [
          _createElementVNode("trailhand-text-input", {
            value: catalogServiceChart.value,
            label: "Chart",
            placeholder: "e.g. mychart",
            required: "",
            onTextInputChange: _cache[4] || (_cache[4] = (e) => { catalogServiceChart.value = e.detail.value; })
          }, null, 40, _hoisted_10)
        ]),
        _cache[25] || (_cache[25] = _createTextVNode()),
        _createElementVNode("trailhand-form-row", _hoisted_11, [
          _createElementVNode("trailhand-text-input", {
            value: catalogServiceChartVersion.value,
            label: "Chart Version",
            placeholder: "1.0.0",
            onTextInputChange: _cache[5] || (_cache[5] = (e) => { catalogServiceChartVersion.value = e.detail.value; })
          }, null, 40, _hoisted_12),
          _cache[15] || (_cache[15] = _createTextVNode()),
          _createElementVNode("trailhand-text-input", {
            value: catalogServiceAppVersion.value,
            label: "App Version",
            placeholder: "1.0.0",
            onTextInputChange: _cache[6] || (_cache[6] = (e) => { catalogServiceAppVersion.value = e.detail.value; })
          }, null, 40, _hoisted_13)
        ]),
        _cache[26] || (_cache[26] = _createTextVNode()),
        _createElementVNode("trailhand-form-row", _hoisted_14, [
          _createElementVNode("trailhand-text-input", {
            value: catalogServiceHelmRepo.value.name,
            label: "Helm Repo Name",
            placeholder: "e.g. my-helm-repo",
            required: "",
            onTextInputChange: _cache[7] || (_cache[7] = (e) => { catalogServiceHelmRepo.value.name = e.detail.value; })
          }, null, 40, _hoisted_15)
        ]),
        _cache[27] || (_cache[27] = _createTextVNode()),
        _createElementVNode("trailhand-form-row", _hoisted_16, [
          _createElementVNode("trailhand-text-input", {
            value: catalogServiceHelmRepo.value.url,
            label: "Helm Repo URL",
            placeholder: "e.g. https://my-helm-repo.com",
            required: true,
            onTextInputChange: _cache[8] || (_cache[8] = (e) => { catalogServiceHelmRepo.value.url = e.detail.value; })
          }, null, 40, _hoisted_17),
          _cache[16] || (_cache[16] = _createTextVNode()),
          _createElementVNode("trailhand-text-input", {
            value: catalogServiceHelmRepo.value.secret,
            label: "Helm Repo Secret",
            placeholder: "e.g. my-helm-repo-secret",
            onTextInputChange: _cache[9] || (_cache[9] = (e) => { catalogServiceHelmRepo.value.secret = e.detail.value; })
          }, null, 40, _hoisted_18)
        ]),
        _cache[28] || (_cache[28] = _createTextVNode()),
        _createVNode(ChartSettings, {
          modelValue: chartSettings.value,
          "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((chartSettings).value = $event))
        }, null, 8, ["modelValue"]),
        _cache[29] || (_cache[29] = _createTextVNode()),
        _createElementVNode("trailhand-form-row", _hoisted_19, [
          _createElementVNode("trailhand-checkbox", {
            checked: showAdvancedOptions.value,
            label: "Show Advanced Options",
            onCheckboxChange: _cache[11] || (_cache[11] = (e) => { showAdvancedOptions.value = e.detail.checked; })
          }, "Show Advanced Options", 40, _hoisted_20)
        ]),
        _cache[30] || (_cache[30] = _createTextVNode()),
        (showAdvancedOptions.value)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
              _cache[18] || (_cache[18] = _createElementVNode("h3", null, "Secret Types", -1)),
              _cache[19] || (_cache[19] = _createTextVNode()),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(catalogServiceSecretTypes.value, (value, index) => {
                return (_openBlock(), _createElementBlock("trailhand-form-row", {
                  key: index,
                  columns: "1"
                }, [
                  _createElementVNode("div", _hoisted_21, [
                    _createElementVNode("trailhand-text-input", {
                      value: value,
                      placeholder: "e.g. database-credentials",
                      style: {"flex":"1"},
                      onTextInputChange: (e) => {
                  const newValue = e.detail.value;
                  catalogServiceSecretTypes.value[index] = newValue;
                }
                    }, null, 40, _hoisted_22),
                    _cache[17] || (_cache[17] = _createTextVNode()),
                    _createElementVNode("trailhand-button", {
                      variant: "destructive",
                      onButtonClick: ($event: any) => (catalogServiceSecretTypes.value = catalogServiceSecretTypes.value.filter((_, i) => i !== index))
                    }, "\n                Remove\n              ", 40, _hoisted_23)
                  ])
                ]))
              }), 128)),
              _cache[20] || (_cache[20] = _createTextVNode()),
              _createElementVNode("div", null, [
                _createElementVNode("trailhand-button", {
                  variant: "alternate",
                  onButtonClick: _cache[12] || (_cache[12] = ($event: any) => (catalogServiceSecretTypes.value.push('')))
                }, "\n              Add Secret Type\n            ", 32)
              ])
            ], 64))
          : _createCommentVNode("", true)
      ]),
      _cache[31] || (_cache[31] = _createTextVNode()),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(errors.value, (err, i) => {
        return (_openBlock(), _createBlock(Banner, {
          key: i,
          color: "error",
          label: err
        }, null, 8, ["label"]))
      }), 128))
    ]),
    _cache[37] || (_cache[37] = _createTextVNode()),
    _createElementVNode("div", _hoisted_24, [
      (isView.value)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            _createElementVNode("trailhand-button", {
              variant: "secondary",
              class: "mr-10",
              onButtonClick: closeModal
            }, "\n          Close\n        ", 32),
            _cache[32] || (_cache[32] = _createTextVNode()),
            _createElementVNode("trailhand-button", {
              variant: "primary",
              onButtonClick: _cache[13] || (_cache[13] = ($event: any) => (modalMode.value = 'edit'))
            }, "\n          Edit Configuration\n        ", 32)
          ], 64))
        : (showDiscardConfirm.value)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
              _cache[33] || (_cache[33] = _createElementVNode("span", { class: "discard-message" }, "You have unsaved changes.", -1)),
              _cache[34] || (_cache[34] = _createTextVNode()),
              _createElementVNode("trailhand-button", {
                variant: "secondary",
                class: "mr-10",
                onButtonClick: handleKeepEditing
              }, "\n          Keep Editing\n        ", 32),
              _cache[35] || (_cache[35] = _createTextVNode()),
              _createElementVNode("trailhand-button", {
                variant: "destructive",
                onButtonClick: handleDiscard
              }, "\n          Discard\n        ", 32)
            ], 64))
          : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
              _createElementVNode("trailhand-button", {
                variant: "secondary",
                class: "mr-10",
                onButtonClick: handleModalClose
              }, "\n          Cancel\n        ", 32),
              _cache[36] || (_cache[36] = _createTextVNode()),
              _createElementVNode("trailhand-button", {
                variant: "primary",
                disabled: !canSave.value,
                onButtonClick: onSubmit
              }, _toDisplayString(saving.value ? (isEdit.value ? 'Saving...' : 'Creating...') : (isEdit.value ? _unref(t)('generic.save') : _unref(t)('generic.create'))), 41, _hoisted_25)
            ], 64))
    ])
  ], 40, _hoisted_1))
}
}

})